import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SovrynnLogo } from './Brand';
import { 
  LayoutDashboard, 
  FileText, 
  Truck, 
  Users,
  Map, 
  ShieldCheck, 
  Calendar,
  Eye,
  ClipboardList,
  BarChart3,
  Settings as SettingsIcon,
  LifeBuoy,
  Crown,
  LogOut,
  Menu,
  X
} from 'lucide-react';
import posthog from 'posthog-js';
import { getSubscriptionTier, SUBSCRIPTION_UPDATED_EVENT } from '../utils/subscription';
import { getDriverLimit } from '../config/pricingPublic';
import '../App.css';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  
  useEffect(() => {
    const readUser = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
    };
    readUser();
    window.addEventListener('focus', readUser);
    window.addEventListener('storage', readUser);
    window.addEventListener(SUBSCRIPTION_UPDATED_EVENT, readUser);
    return () => {
      window.removeEventListener('focus', readUser);
      window.removeEventListener('storage', readUser);
      window.removeEventListener(SUBSCRIPTION_UPDATED_EVENT, readUser);
    };
  }, []);
  
  const handleLogout = () => {
    posthog.reset();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };
  
  const driverLimit = getDriverLimit(getSubscriptionTier(user));

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/documents', icon: FileText, label: 'Documents' },
    { path: '/vehicles', icon: Truck, label: 'Vehicles' },
    ...(driverLimit > 0 ? [{ path: '/drivers', icon: Users, label: 'Drivers' }] : []),
    { path: '/ifta', icon: Map, label: 'IFTA' },
    { path: '/authority', icon: ShieldCheck, label: 'Authority' },
    { path: '/checklists', icon: ClipboardList, label: 'Audit Readiness' },
    { path: '/timeline', icon: Calendar, label: 'Timeline' },
    { path: '/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/counterparty', icon: Eye, label: 'How Others See You' },
    { path: '/support', icon: LifeBuoy, label: 'Support' },
    { path: '/settings', icon: SettingsIcon, label: 'Settings' },
  ];
  
  const SidebarContent = () => (
    <>
      <div className="sidebar-logo">
        <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center' }}>
          <SovrynnLogo size="sm" />
        </Link>
      </div>
      
      <nav className="sidebar-nav">
        {navItems.map(item => (
          <Link 
            key={item.path}
            to={item.path}
            className={location.pathname === item.path ? 'active' : ''}
            onClick={() => setMobileOpen(false)}
          >
            <item.icon size={20} />
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>
      
      <div className="sidebar-footer">
        {user && (user.subscriptionTier === 'free' || user.subscription?.tier === 'free') && (
          <button 
            className="sidebar-action-button upgrade"
            onClick={() => {
              navigate('/pricing');
              setMobileOpen(false);
            }}
          >
            <Crown size={20} />
            <span>Upgrade</span>
          </button>
        )}
        {user && (
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">
              {(user.firstName || user.name || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">
                {user.firstName || user.name || 'User'}
              </div>
              <div className="sidebar-user-email">
                {user.email || 'user@sovrynn.com'}
              </div>
            </div>
          </div>
        )}
        
        <button 
          className="sidebar-action-button"
          onClick={handleLogout}
        >
          <LogOut size={20} />
          <span>Logout</span>
        </button>
      </div>
    </>
  );
  
  return (
    <>
      {/* Mobile menu button */}
      <button 
        className="mobile-menu-button"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
      
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setMobileOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        <SidebarContent />
      </aside>
    </>
  );
}


